/**
 * Hybrid v2 Table components.
 *
 * Each exported sub-component renders the fast pure-CSS-class (Tailwind) path by
 * default and transparently falls back to the styled-components path via the
 * documented `needsStyledComponents` single gate — i.e. when a consumer passes
 * styled-system props (px, color, width, …) or extends the component with
 * `styled()` (detected through an injected `theme` prop or the generated
 * `sc-XXXX` className). This mirrors the established Button/Badge hybrid pattern
 * and preserves 100% backward compatibility with the existing styled-components
 * API.
 */

import React, { useId } from "react";
import { type TableV2Props } from "./TableV2Types";
import { needsStyledComponents } from "@sproutsocial/seeds-react-system-props";
import {
  StyledTableV2Container,
  StyledTableV2,
  StyledCaption,
  StyledTableHeader,
  StyledTableBody,
  StyledTableRow,
  StyledTableHeaderCell,
  StyledTableCell,
} from "./styles";
import {
  TableV2ContainerTailwind,
  TableRootTailwind,
  CaptionTailwind,
  TableHeaderTailwind,
  TableBodyTailwind,
  TableRowTailwind,
  TableHeaderCellTailwind,
  TableCellTailwind,
} from "./TableV2Tailwind";

type HybridProps = {
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
};

const TableContainerContext = React.createContext<string | null>(null);

export const useTableContainerId = () =>
  React.useContext(TableContainerContext);

export const TableWrapper = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactElement } & HybridProps
>(({ children, className, ...rest }, ref) => {
  const containerId = useId();

  // Region semantics stay identical on both paths.
  const containerProps = {
    role: "region",
    tabIndex: 0,
    id: containerId,
    "aria-labelledby": containerId,
  };

  return (
    <TableContainerContext.Provider value={containerId}>
      {needsStyledComponents({ ...rest, className }) ? (
        <StyledTableV2Container
          className={className}
          ref={ref}
          {...containerProps}
          {...rest}
        >
          {children}
        </StyledTableV2Container>
      ) : (
        <TableV2ContainerTailwind
          className={className}
          ref={ref}
          {...containerProps}
          {...rest}
        >
          {children}
        </TableV2ContainerTailwind>
      )}
    </TableContainerContext.Provider>
  );
});

TableWrapper.displayName = "TableV2.TableWrapper";

export const TableRoot = React.forwardRef<HTMLTableElement, HybridProps>(
  ({ className, children, ...rest }, ref) => {
    if (needsStyledComponents({ ...rest, className })) {
      return (
        <StyledTableV2 className={className} ref={ref} {...rest}>
          {children}
        </StyledTableV2>
      );
    }
    return (
      <TableRootTailwind className={className} ref={ref} {...rest}>
        {children}
      </TableRootTailwind>
    );
  }
);

TableRoot.displayName = "TableV2.Table";

export const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  HybridProps & { isVisible?: boolean }
>(({ className, children, isVisible = true, ...rest }, ref) => {
  if (needsStyledComponents({ ...rest, className })) {
    return (
      <StyledCaption
        className={className}
        isVisible={isVisible}
        ref={ref}
        {...rest}
      >
        {children}
      </StyledCaption>
    );
  }
  return (
    <CaptionTailwind
      className={className}
      isVisible={isVisible}
      ref={ref}
      {...rest}
    >
      {children}
    </CaptionTailwind>
  );
});

TableCaption.displayName = "TableV2.Caption";

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  HybridProps
>(({ className, children, ...rest }, ref) => {
  if (needsStyledComponents({ ...rest, className })) {
    return (
      <StyledTableHeader className={className} ref={ref} {...rest}>
        {children}
      </StyledTableHeader>
    );
  }
  return (
    <TableHeaderTailwind className={className} ref={ref} {...rest}>
      {children}
    </TableHeaderTailwind>
  );
});

TableHeader.displayName = "TableV2.TableHeader";

export const TableBody = React.forwardRef<HTMLTableSectionElement, HybridProps>(
  ({ className, children, ...rest }, ref) => {
    if (needsStyledComponents({ ...rest, className })) {
      return (
        <StyledTableBody className={className} ref={ref} {...rest}>
          {children}
        </StyledTableBody>
      );
    }
    return (
      <TableBodyTailwind className={className} ref={ref} {...rest}>
        {children}
      </TableBodyTailwind>
    );
  }
);

TableBody.displayName = "TableV2.TableBody";

export const TableRow = React.forwardRef<HTMLTableRowElement, HybridProps>(
  ({ className, children, ...rest }, ref) => {
    if (needsStyledComponents({ ...rest, className })) {
      return (
        <StyledTableRow className={className} ref={ref} {...rest}>
          {children}
        </StyledTableRow>
      );
    }
    return (
      <TableRowTailwind className={className} ref={ref} {...rest}>
        {children}
      </TableRowTailwind>
    );
  }
);

TableRow.displayName = "TableV2.TableRow";

export const TableHeaderCell = React.forwardRef<
  HTMLTableCellElement,
  HybridProps
>(({ className, children, ...rest }, ref) => {
  if (needsStyledComponents({ ...rest, className })) {
    return (
      <StyledTableHeaderCell className={className} ref={ref} {...rest}>
        {children}
      </StyledTableHeaderCell>
    );
  }
  return (
    <TableHeaderCellTailwind className={className} ref={ref} {...rest}>
      {children}
    </TableHeaderCellTailwind>
  );
});

TableHeaderCell.displayName = "TableV2.TableHeaderCell";

export const TableCell = React.forwardRef<HTMLTableCellElement, HybridProps>(
  ({ className, children, ...rest }, ref) => {
    if (needsStyledComponents({ ...rest, className })) {
      return (
        <StyledTableCell className={className} ref={ref} {...rest}>
          {children}
        </StyledTableCell>
      );
    }
    return (
      <TableCellTailwind className={className} ref={ref} {...rest}>
        {children}
      </TableCellTailwind>
    );
  }
);

TableCell.displayName = "TableV2.TableCell";

const TableV2 = ({
  caption,
  displayCaption = true,
  columns,
  data,
}: TableV2Props) => {
  const containerId = useTableContainerId();

  return (
    <TableWrapper>
      <TableRoot>
        <TableCaption id={containerId ?? undefined} isVisible={displayCaption}>
          {caption}
        </TableCaption>
        <TableHeader>
          <TableRow>
            {columns.map((column, index) => (
              <TableHeaderCell key={index} scope="col">
                {column}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <TableCell key={cellIndex}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </TableRoot>
    </TableWrapper>
  );
};

// Export the full Table component as Table (main export)
export const Table = TableV2;

// Default export
export default TableV2;
